import { useState } from "react";
import { useTranslation } from "../contexts/TranslationContext.jsx";
import {
  Mic,
  Keyboard,
  EyeOff,
  Code,
  GraduationCap,
  NotebookPen,
  Camera,
  Check,
  X,
  Eye,
  Monitor,
  ShieldAlert,
  ShieldCheck,
  LayoutGrid,
  Mail,
} from "lucide-react";

/* ──────────────── Rich text (HTML vindo das traduções) ──────────────── */
function RichP({ html, className = "" }) {
  return <p className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ──────────────── Numbered step ──────────────── */
function Step({ n, title, body, isLast = false }) {
  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-500 text-black font-bold text-xs shrink-0">
          {n}
        </div>
        {!isLast && <div className="w-px flex-1 bg-white/10 mt-2" />}
      </div>
      <div className="pb-8">
        <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
        <div className="text-gray-400 text-sm leading-relaxed space-y-2">
          <RichP html={body} />
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Feature card ──────────────── */
function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-[#111] border border-white/10 rounded-xl p-5 hover:border-brand-500/40 transition">
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-500/10 text-brand-500 shrink-0">
          <Icon size={18} />
        </div>
        <h3 className="text-white font-semibold">{title}</h3>
      </div>
      <RichP html={body} className="text-gray-400 text-sm leading-relaxed" />
    </div>
  );
}

/* ──────────────── "Recomendado para" list ──────────────── */
function UsesList({ heading, uses, className = "" }) {
  return (
    <div className={`bg-[#111] border border-white/10 rounded-xl p-5 ${className}`}>
      <h4 className="text-white font-bold text-base mb-4 pb-3 border-b border-white/10">
        {heading}
      </h4>
      <ul className="space-y-2">
        {uses.map((u, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
            <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
            <span className="leading-relaxed">{u}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ──────────────── Keyboard shortcut row ──────────────── */
function Kbd({ children }) {
  return (
    <kbd className="inline-block px-2 py-0.5 rounded bg-white/10 border border-white/10 text-xs font-mono text-gray-200 whitespace-nowrap">
      {children}
    </kbd>
  );
}
function ShortcutRow({ keys, desc }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-white/5 last:border-0">
      <span className="text-gray-400 text-sm">{desc}</span>
      <Kbd>{keys}</Kbd>
    </div>
  );
}

/* ──────────────── Do / Don't row ──────────────── */
function DoRow({ html }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-gray-300">
      <Check size={16} className="text-emerald-500 shrink-0 mt-0.5" />
      <span className="leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />
    </li>
  );
}
function DontRow({ html }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-gray-300">
      <X size={16} className="text-red-500 shrink-0 mt-0.5" />
      <span className="leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />
    </li>
  );
}

/* ──────────────── Screenshot with caption ──────────────── */
function Figure({ src, alt, caption }) {
  return (
    <figure className="my-2">
      <div className="rounded-xl overflow-hidden border border-white/10 bg-[#0d0d0d] shadow-lg shadow-black/40">
        <img src={src} alt={alt} loading="lazy" className="w-full h-auto block" />
      </div>
      {caption && (
        <figcaption className="text-gray-500 text-xs mt-2 text-center px-4 leading-relaxed">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/* ──────────────── Tabs de topo (underline) ──────────────── */
function TopTabs({ items, active, onChange }) {
  return (
    <div className="flex flex-wrap gap-6 border-b border-white/10 mb-10">
      {items.map(({ id, icon: Icon, label }) => {
        const isActive = id === active;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={`flex items-center gap-2 pb-3 -mb-px text-sm font-semibold border-b-2 transition ${
              isActive
                ? "border-brand-500 text-white"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            <Icon size={16} className={isActive ? "text-brand-500" : ""} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ModeIntro({ html }) {
  return (
    <RichP html={html} className="text-gray-300 text-sm leading-relaxed mb-6" />
  );
}

/* ──────────────── Modos ──────────────── */
const MODES = [
  { id: "entrevista", icon: Mic },
  { id: "reuniao", icon: NotebookPen },
  { id: "codigo", icon: Code },
  { id: "pratica", icon: GraduationCap },
];

function EntrevistaTab({ t }) {
  const base = "tutorial.modes.entrevista";
  return (
    <>
      <ModeIntro html={t(`${base}.intro`)} />
      <div className="mt-6">
        <Step n={1} title={t(`${base}.steps.s1.title`)} body={t(`${base}.steps.s1.body`)} />
        <Step n={2} title={t(`${base}.steps.s2.title`)} body={t(`${base}.steps.s2.body`)} />
        <Step n={3} title={t(`${base}.steps.s3.title`)} body={t(`${base}.steps.s3.body`)} />
        <Step n={4} title={t(`${base}.steps.s4.title`)} body={t(`${base}.steps.s4.body`)} isLast />
      </div>
      <UsesList
        heading={t("tutorial.usesHeading")}
        uses={t(`${base}.uses`, { returnObjects: true })}
      />
    </>
  );
}

function ReuniaoTab({ t }) {
  const base = "tutorial.modes.reuniao";
  return (
    <>
      <ModeIntro html={t(`${base}.intro`)} />
      <div className="mt-6">
        <Step n={1} title={t(`${base}.steps.s1.title`)} body={t(`${base}.steps.s1.body`)} />
        <Step n={2} title={t(`${base}.steps.s2.title`)} body={t(`${base}.steps.s2.body`)} />
        <Step n={3} title={t(`${base}.steps.s3.title`)} body={t(`${base}.steps.s3.body`)} />
        <Step n={4} title={t(`${base}.steps.s4.title`)} body={t(`${base}.steps.s4.body`)} isLast />
      </div>

      <div className="flex items-start gap-3 bg-[#111] border border-white/10 rounded-xl p-4 mt-2">
        <Mail size={18} className="text-brand-500 shrink-0 mt-0.5" />
        <RichP html={t(`${base}.mailNote`)} className="text-gray-400 text-sm leading-relaxed" />
      </div>

      <UsesList
        className="mt-6"
        heading={t("tutorial.usesHeading")}
        uses={t(`${base}.uses`, { returnObjects: true })}
      />
    </>
  );
}

function CodigoTab({ t }) {
  const base = "tutorial.modes.codigo";
  return (
    <>
      <ModeIntro html={t(`${base}.intro`)} />
      <div className="mt-6">
        <Step n={1} title={t(`${base}.steps.s1.title`)} body={t(`${base}.steps.s1.body`)} />
        <Step n={2} title={t(`${base}.steps.s2.title`)} body={t(`${base}.steps.s2.body`)} />
        <Step n={3} title={t(`${base}.steps.s3.title`)} body={t(`${base}.steps.s3.body`)} />
        <Step n={4} title={t(`${base}.steps.s4.title`)} body={t(`${base}.steps.s4.body`)} isLast />
      </div>
      <UsesList
        heading={t("tutorial.usesHeading")}
        uses={t(`${base}.uses`, { returnObjects: true })}
      />
    </>
  );
}

function PraticaTab({ t }) {
  const base = "tutorial.modes.pratica";
  return (
    <>
      <ModeIntro html={t(`${base}.intro`)} />
      <div className="mt-6">
        <Step n={1} title={t(`${base}.steps.s1.title`)} body={t(`${base}.steps.s1.body`)} />
        <Step n={2} title={t(`${base}.steps.s2.title`)} body={t(`${base}.steps.s2.body`)} isLast />
      </div>
      <UsesList
        heading={t("tutorial.usesHeading")}
        uses={t(`${base}.uses`, { returnObjects: true })}
      />
    </>
  );
}

const MODE_CONTENT = {
  entrevista: EntrevistaTab,
  reuniao: ReuniaoTab,
  codigo: CodigoTab,
  pratica: PraticaTab,
};

function ModosPanel({ t }) {
  return (
    <div className="space-y-14">
      {MODES.map(({ id, icon: Icon }, i) => {
        const Content = MODE_CONTENT[id];
        return (
          <div key={id}>
            {i > 0 && <div className="border-t border-white/10 mb-14" />}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 shrink-0">
                <Icon size={20} />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t(`tutorial.modes.${id}.label`)}
              </h2>
            </div>
            <Content t={t} />
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────── Interface ──────────────── */
function InterfacePanel({ t }) {
  const base = "tutorial.interface";
  return (
    <div>
      <RichP html={t(`${base}.intro`)} className="text-gray-400 text-sm mb-6" />
      <Figure
        src="/images/2.png"
        alt={t(`${base}.alt`)}
        caption={t(`${base}.caption`)}
      />
      <h3 className="text-white font-semibold text-sm mt-6 mb-3">
        {t(`${base}.toolbarHeading`)}
      </h3>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 bg-[#111] border border-white/10 rounded-xl p-6 text-sm">
        <ShortcutRow keys="←" desc={t(`${base}.toolbar.back`)} />
        <ShortcutRow keys="⚡" desc={t(`${base}.toolbar.xray`)} />
        <ShortcutRow keys="☰" desc={t(`${base}.toolbar.menu`)} />
        <ShortcutRow keys="ⓘ" desc={t(`${base}.toolbar.info`)} />
        <ShortcutRow keys="🎨" desc={t(`${base}.toolbar.color`)} />
        <ShortcutRow keys="⌃ / ⌄" desc={t(`${base}.toolbar.font`)} />
        <ShortcutRow keys="🗑" desc={t(`${base}.toolbar.clear`)} />
        <ShortcutRow keys="🕘" desc={t(`${base}.toolbar.history`)} />
        <ShortcutRow keys="📦" desc={t(`${base}.toolbar.replay`)} />
        <ShortcutRow keys="🎓" desc={t(`${base}.toolbar.mock`)} />
        <ShortcutRow keys="Credits" desc={t(`${base}.toolbar.credits`)} />
      </div>

      <h3 className="text-white font-semibold text-sm mt-6 mb-3">
        {t(`${base}.menuHeading`)}
      </h3>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 bg-[#111] border border-white/10 rounded-xl p-6 text-sm">
        <ShortcutRow keys="Monitor ▾" desc={t(`${base}.menu.monitor`)} />
        <ShortcutRow keys="Auto-detect ▾" desc={t(`${base}.menu.lang`)} />
      </div>
    </div>
  );
}

/* ──────────────── Atalhos ──────────────── */
function AtalhosPanel({ t }) {
  const base = "tutorial.shortcuts";
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-x-10 gap-y-1 bg-[#111] border border-white/10 rounded-xl p-6">
        <div>
          <h4 className="flex items-center gap-2 text-white font-semibold mb-2 text-sm">
            <Mic size={15} className="text-brand-500" /> {t(`${base}.recHeading`)}
          </h4>
          <ShortcutRow keys="Ctrl/Cmd+Shift+Z" desc={t(`${base}.recording.start`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+X" desc={t(`${base}.recording.stop`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+S" desc={t(`${base}.recording.screenshot`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+V" desc={t(`${base}.recording.paste`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+R" desc={t(`${base}.recording.solve`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+T" desc={t(`${base}.recording.autotype`)} />
        </div>
        <div>
          <h4 className="flex items-center gap-2 text-white font-semibold mb-2 text-sm mt-6 md:mt-0">
            <EyeOff size={15} className="text-brand-500" /> {t(`${base}.winHeading`)}
          </h4>
          <ShortcutRow keys="Ctrl/Cmd+Shift+G" desc={t(`${base}.window.cola`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+F" desc={t(`${base}.window.mini`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+I" desc={t(`${base}.window.xray`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+N" desc={t(`${base}.window.notes`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+P" desc={t(`${base}.window.snippet`)} />
          <ShortcutRow keys="Ctrl/Cmd+Shift+?" desc={t(`${base}.window.all`)} />
        </div>
      </div>
      <p className="text-gray-500 text-xs mt-3 flex items-center gap-1.5">
        <Keyboard size={13} />
        <span dangerouslySetInnerHTML={{ __html: t(`${base}.footer`) }} />
      </p>
    </div>
  );
}

/* ──────────────── Recomendações ──────────────── */
function RecomendacoesPanel({ t }) {
  const base = "tutorial.recommendations";
  const doItems = t(`${base}.do`, { returnObjects: true });
  const dontItems = t(`${base}.dont`, { returnObjects: true });
  return (
    <div>
      <RichP html={t(`${base}.intro`)} className="text-gray-400 text-sm mb-6" />

      {/* Dica nº 1: print, não copie */}
      <div className="flex items-start gap-3 bg-brand-500/5 border border-brand-500/20 rounded-xl p-5 mb-6">
        <Camera size={20} className="text-brand-500 shrink-0 mt-0.5" />
        <div
          className="text-sm leading-relaxed text-gray-300"
          dangerouslySetInnerHTML={{ __html: t(`${base}.tip`) }}
        />
      </div>

      {/* Faça / Evite */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h4 className="flex items-center gap-2 text-emerald-500 font-bold text-base mb-4 pb-3 border-b border-white/10">
            <Check size={20} /> {t("tutorial.doHeading")}
          </h4>
          <ul className="space-y-2.5">
            {doItems.map((html, i) => (
              <DoRow key={i} html={html} />
            ))}
          </ul>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h4 className="flex items-center gap-2 text-red-500 font-bold text-base mb-4 pb-3 border-b border-white/10">
            <X size={20} /> {t("tutorial.dontHeading")}
          </h4>
          <ul className="space-y-2.5">
            {dontItems.map((html, i) => (
              <DontRow key={i} html={html} />
            ))}
          </ul>
        </div>
      </div>

      {/* Três cenários */}
      <div className="grid md:grid-cols-3 gap-4">
        <FeatureCard
          icon={Monitor}
          title={t(`${base}.cards.share.title`)}
          body={t(`${base}.cards.share.body`)}
        />
        <FeatureCard
          icon={Eye}
          title={t(`${base}.cards.webcam.title`)}
          body={t(`${base}.cards.webcam.body`)}
        />
        <FeatureCard
          icon={ShieldAlert}
          title={t(`${base}.cards.proctor.title`)}
          body={t(`${base}.cards.proctor.body`)}
        />
      </div>
    </div>
  );
}

/* ──────────────── Tabs de topo ──────────────── */
const TOP_TABS = [
  { id: "interface", icon: LayoutGrid },
  { id: "modos", icon: Mic },
  { id: "atalhos", icon: Keyboard },
  { id: "recomendacoes", icon: ShieldCheck },
];

const TOP_CONTENT = {
  interface: InterfacePanel,
  modos: ModosPanel,
  atalhos: AtalhosPanel,
  recomendacoes: RecomendacoesPanel,
};

export default function Tutorial() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("interface");
  const ActivePanel = TOP_CONTENT[activeTab];

  const topTabs = TOP_TABS.map((tab) => ({
    ...tab,
    label: t(`tutorial.tabs.${tab.id}`),
  }));

  return (
    <div className="flex-1 bg-[#0a0a0a] text-white px-6 pt-12 pb-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-6">
          {t("tutorial.title")}
        </h1>

        <TopTabs items={topTabs} active={activeTab} onChange={setActiveTab} />

        <ActivePanel t={t} />
      </div>
    </div>
  );
}
